/**
 * Converts raw technical error messages into simple, user-friendly text.
 * Used across the app to ensure end-users never see code-level jargon.
 */

const ERROR_MAP: [RegExp, string][] = [
    // Network / connectivity
    [/Failed to fetch|NetworkError|net::ERR|ECONNREFUSED|ERR_NETWORK/i,
        '⚠️ Unable to connect to the server. Please check your internet connection and try again.'],
    [/timeout|ETIMEDOUT/i,
        '⏳ The request took too long. Please check your internet and try again.'],

    // Firebase / Firestore
    [/permission.denied|PERMISSION_DENIED/i,
        '🔒 You do not have permission to perform this action. Please contact your admin.'],
    [/not.found|NOT_FOUND|No document to update/i,
        '❌ The requested record was not found. It may have been deleted.'],
    [/already.exists|ALREADY_EXISTS/i,
        '⚠️ This record already exists. Please use a different name or code.'],
    [/quota.exceeded|RESOURCE_EXHAUSTED/i,
        '⚠️ System limit reached. Please try again later or contact support.'],
    [/unauthenticated|UNAUTHENTICATED|auth\/.*expired/i,
        '🔐 Your session has expired. Please log in again.'],
    [/unavailable|UNAVAILABLE/i,
        '⚠️ Service is temporarily unavailable. Please try again in a moment.'],

    // FBR / IRIS specific
    [/400.*Bad Request|validation.*fail/i,
        '⚠️ FBR rejected the invoice data. Please check item details (HS codes, quantities, prices) and try again.'],
    [/401.*Unauthorized|403.*Forbidden/i,
        '🔐 FBR authentication failed. Your POS credentials may need to be refreshed. Please contact support.'],
    [/500.*Internal Server Error/i,
        '⚠️ FBR server is experiencing issues. Please try again in a few minutes.'],
    [/503.*Service Unavailable/i,
        '⚠️ FBR (IRIS) service is temporarily down. Please try submitting later.'],
    [/Buyer registration.*invalid|BuyerNTN.*invalid|BuyerCNIC.*invalid/i,
        '⚠️ The buyer NTN or CNIC number format is not valid. Please double-check and correct.'],
    [/HS.?Code.*invalid|PCTCode/i,
        '⚠️ One or more item HS Codes are invalid. Please update your product HS codes.'],

    // Cloudinary / upload
    [/upload.*fail|cloudinary/i,
        '⚠️ Image upload failed. Please check your connection and try again.'],

    // Generic database ops
    [/Error saving|Error creating|write.*failed/i,
        '❌ Could not save the record. Please try again.'],
    [/Error fetching|Error loading|read.*failed/i,
        '⚠️ Could not load data. Please refresh the page and try again.'],
    [/Error deleting|delete.*failed/i,
        '❌ Could not delete the record. Please try again.'],
];

export function friendlyError(error: any, context?: string): string {
    const raw = typeof error === 'string' ? error : (error?.message || error?.toString() || 'Unknown error');

    // Check against known error patterns
    for (const [pattern, friendly] of ERROR_MAP) {
        if (pattern.test(raw)) {
            return friendly;
        }
    }

    // Fallback: strip technical noise and return a clean message
    const contextPrefix = context ? `${context}: ` : '';

    // If the message is short and readable (no stack trace), show it
    if (raw.length < 120 && !raw.includes('at ') && !raw.includes('Error:')) {
        return `⚠️ ${contextPrefix}${raw}`;
    }

    // Otherwise give a generic message
    return `⚠️ ${contextPrefix}Something went wrong. Please try again. If the problem continues, contact support.`;
}
